"use client";
import CircularOutlineIcon from "@/components/CircularButton/styled/CircularOutlineIcon";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { Flex, FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import LazyLoaded from "@/components/LazyLoaded";
import { useChatEditorCommunicator } from "@/features/AI/KaiChat/hooks/useChatEditorCommunicator";
import { ChatFlowState } from "@/features/AI/KaiChat/types";
import { SetEditingSortableHandler } from "@/features/FlashcardsEditingPage/components/SetEditingDndHandlers";
import { useSetEditingContextSelector } from "@/features/FlashcardsEditingPage/hooks/useSetEditingContext";
import { FlashcardSortable } from "@/features/FlashcardsEditingPage/types";
import { useUpgradePopupContextSelector } from "@/features/Payments/PaywallPopup/UpgradePopupContext";
import { useSafeQueryNavigation } from "@/hooks/navigation/useSafeQueryNavigation";
import { useContainerBreakPoints } from "@/hooks/styles/useBreakpoints";
import br from "@/styles/breakpoints.module.css";
import { iconSizes } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { Editor } from "@knowt/editor/react";
import {
    Flashcard,
    FlashcardSet,
    FlashcardSide,
    ItemType,
    Language,
    UpgradeEvent,
} from "@knowt/syncing/graphql/schema";
import { callDeleteAudio } from "@knowt/syncing/hooks/flashcards/graphqlUtils";
import { UseFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { assertTruthy } from "@knowt/syncing/utils/assertions";
import useCombinedState from "@knowt/syncing/utils/hooks/useCombinedState";
import { isEqual } from "lodash-es";
import { Repeat } from "lucide-react";
import { Lightbulb, MoveVertical, Settings2Icon, Trash2 } from "lucide-react";
import React, { memo, useImperativeHandle, useRef, useState, useCallback, useEffect, useMemo, RefObject } from "react";
import AudioInputSection from "../components/AudioInputSection";
import CustomMcOptionsInputSection from "../components/CustomMcOptionsInputSection";
import CustomizationFeatureButton from "../components/CustomizationFeatureButton";
import CustomHintPopup from "./CustomHintPopup";
import FlashcardEditorToolbar from "./FlashcardEditorToolbar";
import { ImageButton, RecordButton } from "./SharedComponents";
import TermAndDefinitionInputs from "./TermAndDefinitionInputs";
import { isFlashcardCardEmpty, mergeTermAndDefinitionText } from "./utils";
import Circular from "@/components/Circular";
import SparkleIcon from "@/components/icons/SparkleIcon";

const TOOLBAR_HEIGHT = "4.3rem";

const Header = ({
    index,
    flashcardId,
    flashcardEditors,
    onDelete,
    disableDelete,
    onSwapTermDefinition,
    currInputFocus,
}: {
    index: number;
    flashcardId: string;
    flashcardEditors: { termEditor: Editor | null; definitionEditor: Editor | null };
    onDelete: (flashcardId: string) => Promise<Flashcard>;
    disableDelete: boolean;
    onSwapTermDefinition: () => void;
    currInputFocus: { term?: boolean; definition?: boolean };
}) => {
    const setIsChatSplitSectionOpen = useSetEditingContextSelector(s => s.setIsChatSplitSectionOpen);
    const isChatSplitSectionOpen = useSetEditingContextSelector(s => s.isChatSplitSectionOpen);
    const { termEditor, definitionEditor } = flashcardEditors;

    const setSelectedContent = useChatEditorCommunicator(s => s.setSelectedContent);
    const ssotFlashcardEditor = useChatEditorCommunicator(s => s.flashcardEditor);
    const setFlashcardEditor = useChatEditorCommunicator(s => s.setFlashcardEditor);
    const handleUnselect = useChatEditorCommunicator(s => s.handleUnselect);
    const setChatFlowState = useChatEditorCommunicator(s => s.setChatFlowState);
    const setIsEmptyFlashcardSelected = useChatEditorCommunicator(s => s.setIsEmptyFlashcardSelected);
    const focusToChatInput = useChatEditorCommunicator(s => s.focusToChatInput);

    const { addParamsSilentReplace } = useSafeQueryNavigation();

    const handleAIClick = () => {
        const termText = termEditor?.getHTML() || "";
        const definitionText = definitionEditor?.getHTML() || "";

        const selectedContent = mergeTermAndDefinitionText({ term: termText, definition: definitionText, index });

        handleUnselect();
        setIsEmptyFlashcardSelected(isFlashcardCardEmpty({ term: termText, definition: definitionText }));
        setFlashcardEditor({ term: termEditor, definition: definitionEditor, flashcardId });
        setChatFlowState(ChatFlowState.SELECTED_TEXT_ACTIONS);
        setSelectedContent(selectedContent);
        setIsChatSplitSectionOpen(true);
        addParamsSilentReplace({ tab: "Chat" });
        focusToChatInput();
    };

    return (
        <React.Fragment>
            <FlexRowAlignCenter
                style={{ justifyContent: "space-between", minHeight: TOOLBAR_HEIGHT, position: "relative" }}>
                <Circular
                    radius="4rem"
                    style={{ backgroundColor: themeColors.background }}
                    className="secondaryTextBold1">
                    {index + 1}
                </Circular>

                <FlexRowAlignCenter style={{ gap: "1rem" }}>
                    <CircularRectTextButton
                        tabIndex={-1}
                        onClick={handleAIClick}
                        tooltip={"Ask kai to write this flashcard for you"}
                        style={{
                            gap: spacing.XXS,
                            background:
                                isChatSplitSectionOpen && ssotFlashcardEditor.flashcardId === flashcardId
                                    ? themeColors.neutralBlack
                                    : themeColors.aiGradientFlip,
                            border:
                                isChatSplitSectionOpen && ssotFlashcardEditor.flashcardId === flashcardId
                                    ? `1px solid ${themeColors.neutralBlack}`
                                    : `1px solid ${themeColors.ai}`,

                            padding: `${spacing.XS} ${spacing.SM}`,
                            display: "flex",
                            alignItems: "center",
                            transition: "background 0.2s ease-in-out ",
                        }}>
                        <SparkleIcon size={iconSizes.SM} />
                        <span
                            className="secondaryTextBold1"
                            style={{
                                color:
                                    isChatSplitSectionOpen && ssotFlashcardEditor.flashcardId === flashcardId
                                        ? themeColors.neutralWhite
                                        : themeColors.pureBlack,
                                transition: "color 0.2s ease-in-out ",
                            }}>
                            Ask Kai
                        </span>
                    </CircularRectTextButton>
                    <SetEditingSortableHandler>
                        <CircularOutlineIcon
                            tabIndex={-1}
                            Icon={MoveVertical}
                            size={iconSizes.SM}
                            borderColor={themeColors.neutral1}
                            hoverColor={themeColors.neutralBlack}
                            hoverBackgroundColor={themeColors.neutral1}
                            tooltip={"Move this flashcard"}
                        />
                    </SetEditingSortableHandler>
                    <CircularOutlineIcon
                        tabIndex={-1}
                        Icon={Repeat}
                        size={iconSizes.SM}
                        onClick={onSwapTermDefinition}
                        borderColor={themeColors.neutral1}
                        hoverColor={themeColors.neutralBlack}
                        hoverBackgroundColor={themeColors.neutral1}
                        tooltip={"Swap term and definition"}
                    />
                    <CircularOutlineIcon
                        tabIndex={-1}
                        Icon={Trash2}
                        size={iconSizes.SM}
                        onClick={async () => {
                            if (!disableDelete && typeof onDelete === "function") {
                                await onDelete(flashcardId);
                            }
                        }}
                        tooltip={disableDelete ? "You need to have at least 4 flashcards" : "Delete this flashcard"}
                        borderColor={themeColors.neutral1}
                        hoverColor={themeColors.neutralBlack}
                        hoverBackgroundColor={themeColors.neutral1}
                    />
                </FlexRowAlignCenter>
            </FlexRowAlignCenter>

            <FlashcardEditorToolbar
                editor={currInputFocus.term ? termEditor : currInputFocus.definition ? definitionEditor : null}
                style={{ minHeight: TOOLBAR_HEIGHT }}
            />
        </React.Fragment>
    );
};

type EditableFlashcardCardProps = {
    index: number;
    flashcard: FlashcardSortable;
    disableDelete: boolean;
    termLanguage?: Language | null;
    definitionLanguage?: Language | null;
    updateFlashcardSetMetaData?: UseFlashcardSet["updateFlashcardSetMetaData"];
    deleteFlashcard?: UseFlashcardSet["deleteFlashcard"];
    pendingUpdates?: RefObject<{
        metadata: Partial<Omit<FlashcardSet, "flashcards">>;
        flashcards: { [flashcardId: string]: Partial<Flashcard> };
    }>;
    isCustomizationBlocked?: boolean;
};

export type EditableFlashcardCardHandle = { forceUpdateContent: (flashcard: Flashcard) => void };

const EditableFlashcardCard = ({
    ref,
    flashcard,
    updateFlashcardSetMetaData,
    deleteFlashcard,
    disableDelete,
    index,
    pendingUpdates,
    isCustomizationBlocked,
    termLanguage,
    definitionLanguage,
}: EditableFlashcardCardProps & {
    ref: React.RefObject<EditableFlashcardCardHandle>;
}) => {
    const { flashcardId } = flashcard;

    const flashcardRef = useRef<HTMLElement>(null);

    const openUpgradePopup = useUpgradePopupContextSelector(state => state.openUpgradePopup);

    const { mdDown } = useContainerBreakPoints({
        container: flashcardRef.current ?? document.body,
        md: 800,
        sm: 350,
    });

    // console.log("RERENDERING EDITABLE CARD");

    // we first update the local values to keep the UX snappy,
    // then at different occasions, we commit these changes to the swr cache
    const [localImage, setLocalImage] = useState(flashcard.image);
    const [localSecondaryImage, setLocalSecondaryImage] = useState(flashcard.secondaryImage);
    const [localMcOptions, setLocalMcOptions] = useState<string[] | null>(flashcard?.mcOptions);
    const [localTermCustomHint, setLocalTermCustomHint] = useState(flashcard?.termCustomHint);
    const [localDefinitionCustomHint, setLocalDefinitionCustomHint] = useState(flashcard?.definitionCustomHint);

    const [isShowingAudioSection, setIsShowingAudioSection] = useState(false);
    const [isShowingCustomMCSection, setIsShowingCustomMCSection] = useState(false);
    const [isCustomHintPopupOpen, setIsCustomHintPopupOpen] = useState(false);

    const [recordingSide, setRecordingSide] = useState<FlashcardSide | null>(null);

    const [currInputFocus, updateCurrInputFocus] = useCombinedState({ term: false, definition: false });
    const isInputFocused = useMemo(() => currInputFocus.term || currInputFocus.definition, [currInputFocus]);

    const [isBottomSectionVisible, setIsBottomSectionVisible] = useState(false);

    const setPendingUpdates = useCallback(
        (updates: Partial<Flashcard>) => {
            assertTruthy(pendingUpdates?.current, "pendingUpdates should be defined");

            pendingUpdates.current.flashcards[flashcardId] = {
                ...pendingUpdates.current.flashcards[flashcardId],
                ...updates,
            };
        },
        [flashcardId, pendingUpdates]
    );

    const handleUpdateLocalTerm = useCallback(
        (term?: string | null) => {
            if (flashcard.termAudio) {
                callDeleteAudio({
                    text: "", // unused
                    flashcardSetId: flashcard.flashcardSetId,
                    flashcardId: flashcard.flashcardId,
                    side: FlashcardSide.TERM,
                });
            }
            setPendingUpdates({ term, termAudio: null, termRecordedAudio: null });
        },
        [flashcard.flashcardId, flashcard.flashcardSetId, flashcard.termAudio, setPendingUpdates]
    );

    const handleUpdateLocalDefinition = useCallback(
        (definition?: string | null) => {
            if (flashcard.definitionAudio) {
                callDeleteAudio({
                    text: "", // unused
                    flashcardSetId: flashcard.flashcardSetId,
                    flashcardId: flashcard.flashcardId,
                    side: FlashcardSide.DEFINITION,
                });
            }
            setPendingUpdates({ definition, definitionAudio: null, definitionRecordedAudio: null });
        },
        [flashcard.definitionAudio, flashcard.flashcardId, flashcard.flashcardSetId, setPendingUpdates]
    );

    const handleUpdateLocalImage = useCallback(
        (image?: string | null) => {
            setLocalImage(image);
            setPendingUpdates({ image });
        },
        [setPendingUpdates]
    );

    const handleUpdateLocalSecondaryImage = useCallback(
        (secondaryImage?: string | null) => {
            setLocalSecondaryImage(secondaryImage);
            setPendingUpdates({ secondaryImage });
        },
        [setPendingUpdates]
    );

    const handleRecordButtonClick = useCallback(
        (side: FlashcardSide) => {
            if (isCustomizationBlocked) {
                return openUpgradePopup({
                    event: UpgradeEvent.CUSTOM_FLASHCARD_AUDIO,
                    context: { itemId: flashcard.flashcardSetId, itemType: ItemType.FLASHCARDSET },
                });
            }

            if (isShowingAudioSection && side === recordingSide) {
                setRecordingSide(null);
                setIsShowingAudioSection(false);
                return;
            }

            setRecordingSide(side);
            setIsShowingAudioSection(true);

            setIsShowingCustomMCSection(false);
        },
        [flashcard.flashcardSetId, isCustomizationBlocked, isShowingAudioSection, openUpgradePopup, recordingSide]
    );

    const handleUpdateLocalAudio = useCallback(
        (audio: string, side: FlashcardSide) => {
            if (side === FlashcardSide.TERM) {
                setPendingUpdates({
                    termRecordedAudio: audio,
                });
            } else {
                setPendingUpdates({
                    definitionRecordedAudio: audio,
                });
            }
        },
        [setPendingUpdates]
    );

    const showCustomMCSection = () => {
        if (isCustomizationBlocked) {
            openUpgradePopup({
                event: UpgradeEvent.CUSTOM_MC,
                context: { itemId: flashcard.flashcardSetId as string },
            });
            return;
        }

        setIsShowingAudioSection(false);
        setRecordingSide(null);
        setIsShowingCustomMCSection(true);
    };

    const handleUpdateLocalMcOptions = useCallback(
        (mcOptionsArray: string[] | null) => {
            setLocalMcOptions(mcOptionsArray);
            setPendingUpdates({
                mcOptions: mcOptionsArray ? mcOptionsArray.filter(opt => opt !== "") : null,
            });
        },
        [setPendingUpdates]
    );

    const handleOpenCustomHintsPopup = useCallback(() => {
        if (isCustomizationBlocked) {
            return openUpgradePopup({
                event: UpgradeEvent.CUSTOM_HINT,
                context: { itemId: flashcard.flashcardSetId, itemType: ItemType.FLASHCARDSET },
            });
        }
        setIsCustomHintPopupOpen(true);
    }, [flashcard.flashcardSetId, isCustomizationBlocked, openUpgradePopup]);

    const handleSaveCustomHints = useCallback(
        (termCustomHint: string | null, definitionCustomHint: string | null) => {
            setLocalTermCustomHint(termCustomHint);
            setLocalDefinitionCustomHint(definitionCustomHint);

            setPendingUpdates({ termCustomHint, definitionCustomHint });
            setIsCustomHintPopupOpen(false);
        },
        [setPendingUpdates]
    );

    const flashcardEditorsRef = useRef<{ termEditor: Editor | null; definitionEditor: Editor | null }>({
        termEditor: null,
        definitionEditor: null,
    });

    useImperativeHandle(ref, () => ({
        forceUpdateContent: (flashcard: Flashcard) => {
            setLocalImage(flashcard.image);
            setLocalSecondaryImage(flashcard.secondaryImage);

            flashcardEditorsRef.current?.termEditor?.commands.setContent(flashcard.term ?? "");
            flashcardEditorsRef.current?.definitionEditor?.commands.setContent(flashcard.definition ?? "");
        },
    }));

    const swapTermAndDefinition = useCallback(async () => {
        handleUpdateLocalImage(localSecondaryImage);
        handleUpdateLocalSecondaryImage(localImage);

        const termContentInEditor = flashcardEditorsRef.current?.termEditor?.getHTML() || "";
        const definitionContentInEditor = flashcardEditorsRef.current?.definitionEditor?.getHTML() || "";

        flashcardEditorsRef.current?.termEditor?.commands.setContent(definitionContentInEditor);
        flashcardEditorsRef.current?.definitionEditor?.commands.setContent(termContentInEditor);

        setPendingUpdates({ term: definitionContentInEditor, definition: termContentInEditor });
    }, [handleUpdateLocalImage, handleUpdateLocalSecondaryImage, localImage, localSecondaryImage, setPendingUpdates]);

    useEffect(() => {
        if (isInputFocused) {
            setIsBottomSectionVisible(true);
        } else {
            // A small delay to allow click events to be captured before hiding the section
            const timer = setTimeout(() => setIsBottomSectionVisible(false), 50);
            return () => clearTimeout(timer);
        }
    }, [isInputFocused]);

    useEffect(() => {
        if (
            (recordingSide === FlashcardSide.TERM && currInputFocus.definition) ||
            (recordingSide === FlashcardSide.DEFINITION && currInputFocus.term)
        ) {
            setIsShowingAudioSection(false);
            setRecordingSide(null);
            return;
        }
    }, [recordingSide, currInputFocus]);

    useEffect(() => {
        if (flashcard.mcOptions) {
            if (isInputFocused && !isShowingAudioSection && !isCustomizationBlocked) {
                setIsShowingCustomMCSection(true);
            }
        }
    }, [isInputFocused, isShowingAudioSection, isCustomizationBlocked, flashcard.mcOptions]);

    const renderTermSection = useCallback(
        (termComponent: React.ReactNode) => (
            <Flex style={{ gap: "inherit", width: mdDown ? "100%" : "50%" }} className={br.smDownColumn}>
                <FlexColumn style={{ margin: "auto", gap: spacing.XS, alignItems: "stretch" }}>
                    <ImageButton
                        tabIndex={-1}
                        image={localSecondaryImage}
                        onImageChange={async ({ image }) => {
                            await handleUpdateLocalSecondaryImage(image);
                        }}
                    />
                    <RecordButton
                        onClick={() => handleRecordButtonClick(FlashcardSide.TERM)}
                        tabIndex={-1}
                        inAction={recordingSide === FlashcardSide.TERM}
                        storedAudio={flashcard.termRecordedAudio}
                        isBlocked={isCustomizationBlocked}
                    />
                </FlexColumn>
                <div style={{ marginTop: "1.3rem", flex: 1 }}>{termComponent}</div>
            </Flex>
        ),
        [
            flashcard.termRecordedAudio,
            handleRecordButtonClick,
            handleUpdateLocalSecondaryImage,
            isCustomizationBlocked,
            localSecondaryImage,
            mdDown,
            recordingSide,
        ]
    );

    const renderDefinitionSection = useCallback(
        (definitionComponent: React.ReactNode) => (
            <Flex
                style={{
                    flexDirection: mdDown ? "row-reverse" : "row",
                    gap: "inherit",
                    width: mdDown ? "100%" : "50%",
                }}
                className={br.smDownColumn}>
                <div style={{ marginTop: "1.3rem", flex: 1 }}>{definitionComponent}</div>
                <FlexColumn style={{ margin: "auto", gap: spacing.XS, alignItems: "stretch" }}>
                    <ImageButton
                        tabIndex={-1}
                        image={localImage}
                        onImageChange={async ({ image }) => {
                            await handleUpdateLocalImage(image);
                        }}
                    />
                    <RecordButton
                        onClick={() => handleRecordButtonClick(FlashcardSide.DEFINITION)}
                        tabIndex={-1}
                        inAction={recordingSide === FlashcardSide.DEFINITION}
                        storedAudio={flashcard.definitionRecordedAudio}
                        isBlocked={isCustomizationBlocked}
                    />
                </FlexColumn>
            </Flex>
        ),
        [
            flashcard.definitionRecordedAudio,
            handleRecordButtonClick,
            handleUpdateLocalImage,
            isCustomizationBlocked,
            localImage,
            mdDown,
            recordingSide,
        ]
    );

    return (
        <FlexColumn
            ref={flashcardRef}
            style={{
                backgroundColor: themeColors.neutralWhite,
                borderRadius: "2rem",
                padding: "2.2rem",
                gap: "2rem",
                width: "100%",
                marginLeft: "auto",
                marginRight: "auto",
            }}>
            <Header
                flashcardEditors={{
                    termEditor: flashcardEditorsRef.current?.termEditor,
                    definitionEditor: flashcardEditorsRef.current?.definitionEditor,
                }}
                flashcardId={flashcard.flashcardId}
                disableDelete={disableDelete}
                index={index}
                onSwapTermDefinition={swapTermAndDefinition}
                currInputFocus={currInputFocus}
                onDelete={deleteFlashcard}
            />

            <Flex style={{ gap: "2rem", flexDirection: mdDown ? "column" : "row" }}>
                <TermAndDefinitionInputs
                    ref={flashcardEditorsRef}
                    updateFlashcardSetMetaData={updateFlashcardSetMetaData}
                    flashcard={flashcard}
                    initialTerm={flashcard.term || ""}
                    initialDefinition={flashcard.definition || ""}
                    handleUpdateTerm={handleUpdateLocalTerm}
                    handleUpdateDefinition={handleUpdateLocalDefinition}
                    handleUpdateImage={handleUpdateLocalImage}
                    handleUpdateSecondaryImage={handleUpdateLocalSecondaryImage}
                    renderTermSection={renderTermSection}
                    renderDefinitionSection={renderDefinitionSection}
                    termLanguage={termLanguage}
                    definitionLanguage={definitionLanguage}
                    setCurrInputFocus={updateCurrInputFocus}
                    currInputFocus={currInputFocus}
                />
            </Flex>

            <FlexRowAlignCenter style={{ justifyContent: "flex-end", gap: spacing.XS, height: "2rem" }}>
                {isBottomSectionVisible && (
                    <>
                        {!isShowingCustomMCSection && (
                            <CustomizationFeatureButton
                                onClick={showCustomMCSection}
                                icon={<Settings2Icon size={16} color={themeColors.neutralBlack} />}
                                text={"Add multiple choice options"}
                                activeText={"Custom multiple choice on"}
                                isActive={!!flashcard.mcOptions}
                                isBlocked={isCustomizationBlocked}
                            />
                        )}
                        <CustomizationFeatureButton
                            onClick={handleOpenCustomHintsPopup}
                            icon={<Lightbulb size={16} color={themeColors.neutralBlack} />}
                            text={"Add custom hints"}
                            isActive={!!flashcard.termCustomHint || !!flashcard.definitionCustomHint}
                            activeText={"Custom hints on"}
                            isBlocked={isCustomizationBlocked}
                        />
                    </>
                )}
            </FlexRowAlignCenter>

            <LazyLoaded load={isShowingAudioSection}>
                <AudioInputSection
                    flashcard={flashcard}
                    definitionKnowtAudio={flashcard.definitionAudio}
                    isHidden={!isShowingAudioSection}
                    recordingSide={recordingSide}
                    getAudioSideReady={(side: FlashcardSide) => setRecordingSide(side)}
                    termAudio={flashcard.termRecordedAudio}
                    definitionAudio={flashcard.definitionRecordedAudio}
                    onChange={handleUpdateLocalAudio}
                />
            </LazyLoaded>

            <LazyLoaded load={isShowingCustomMCSection}>
                <CustomMcOptionsInputSection
                    flashcardId={flashcard.flashcardId}
                    isHidden={!isShowingCustomMCSection}
                    localMcOptions={localMcOptions}
                    onChange={handleUpdateLocalMcOptions}
                    onRemoveOptions={() => setIsShowingCustomMCSection(false)}
                />
            </LazyLoaded>

            <LazyLoaded load={isCustomHintPopupOpen}>
                <CustomHintPopup
                    isOpen={isCustomHintPopupOpen}
                    onClose={() => setIsCustomHintPopupOpen(false)}
                    defaultTermHint={localTermCustomHint}
                    defaultDefinitionHint={localDefinitionCustomHint}
                    termLanguage={termLanguage}
                    defLanguage={definitionLanguage}
                    onSave={handleSaveCustomHints}
                />
            </LazyLoaded>
        </FlexColumn>
    );
};

EditableFlashcardCard.displayName = "EditableFlashcardCard";

export default memo(EditableFlashcardCard, (prevProps, nextProps) => isEqual(prevProps, nextProps));
